/**
 * The chrome around a glossary page, in five languages. One file, same
 * reasoning as `tools/ui.ts`.
 */

import type { ContentLang } from "@fazly/shared/contentLang";

export interface GlossaryUi {
  /**
   * Alt text for the diagram. `%s` is the term.
   *
   * It says the drawing is on a REFERENCE face, not the reader's: the face is
   * Google's canonical model, and a caption implying otherwise would be the one
   * dishonest sentence on a page about what is and is not measured.
   */
  readonly diagramLabel: string;
  readonly measureIt: string;
  readonly noTool: string;
  readonly seeAlso: string;
  readonly backToGlossary: string;
  readonly indexTitle: string;
  readonly indexDescription: string;
  readonly indexH1: string;
  readonly indexStandfirst: string;
}

export const GLOSSARY_UI: Record<ContentLang, GlossaryUi> = {
  en: {
    diagramLabel: "%s, drawn on a reference face",
    measureIt: "Measure this on your own face",
    noTool:
      "This one cannot be measured from a front-facing photograph, so there is no tool for it here.",
    seeAlso: "See also",
    backToGlossary: "All terms",
    indexTitle: "Looksmax Glossary — 21 Face-Rating Terms Explained | Fazly",
    indexDescription:
      "Every term you need to read a face report, defined plainly: canthal tilt, fWHR, gonial angle, mewing, PSL and 16 more — with what is and is not supported.",
    indexH1: "The looksmax glossary",
    indexStandfirst:
      "Twenty-one terms, defined plainly, with the folklore separated from the geometry.",
  },
  tr: {
    diagramLabel: "%s, referans bir yüz üzerinde çizilmiş",
    measureIt: "Bunu kendi yüzünde ölç",
    noTool:
      "Bu, önden çekilmiş bir fotoğraftan ölçülemiyor; dolayısıyla burada onun için bir araç yok.",
    seeAlso: "Ayrıca bakınız",
    backToGlossary: "Tüm terimler",
    indexTitle: "Looksmax Sözlüğü — 21 Yüz Değerlendirme Terimi | Fazly",
    indexDescription:
      "Bir yüz raporunu okumak için gereken her terim, sade biçimde tanımlandı: canthal tilt, fWHR, gonial açı, mewing, PSL ve 16 tane daha — neyin desteklendiğiyle birlikte.",
    indexH1: "Looksmax sözlüğü",
    indexStandfirst:
      "Yirmi bir terim, sade tanımlar ve folklorun geometriden ayrıldığı yer.",
  },
  de: {
    diagramLabel: "%s, auf einem Referenzgesicht eingezeichnet",
    measureIt: "Miss das an deinem eigenen Gesicht",
    noTool:
      "Das lässt sich aus einem Frontalfoto nicht messen, deshalb gibt es hier kein Werkzeug dafür.",
    seeAlso: "Siehe auch",
    backToGlossary: "Alle Begriffe",
    indexTitle: "Looksmax-Glossar — 21 Begriffe zur Gesichtsbewertung | Fazly",
    indexDescription:
      "Jeder Begriff, den du zum Lesen eines Gesichtsberichts brauchst, klar definiert: Canthal Tilt, fWHR, Gonialwinkel, Mewing, PSL und 16 weitere.",
    indexH1: "Das Looksmax-Glossar",
    indexStandfirst:
      "Einundzwanzig Begriffe, klar definiert, mit der Folklore von der Geometrie getrennt.",
  },
  es: {
    diagramLabel: "%s, dibujado sobre una cara de referencia",
    measureIt: "Mide esto en tu propia cara",
    noTool:
      "Esto no se puede medir desde una foto de frente, así que aquí no hay una herramienta para ello.",
    seeAlso: "Véase también",
    backToGlossary: "Todos los términos",
    indexTitle: "Glosario Looksmax — 21 Términos de Análisis Facial | Fazly",
    indexDescription:
      "Todos los términos que necesitas para leer un informe facial, definidos con claridad: canthal tilt, fWHR, ángulo gonial, mewing, PSL y 16 más.",
    indexH1: "El glosario looksmax",
    indexStandfirst:
      "Veintiún términos, definidos con claridad, con el folclore separado de la geometría.",
  },
  pt: {
    diagramLabel: "%s, desenhado sobre um rosto de referência",
    measureIt: "Meça isso no seu próprio rosto",
    noTool:
      "Isto não pode ser medido a partir de uma foto de frente, então não há uma ferramenta para isso aqui.",
    seeAlso: "Veja também",
    backToGlossary: "Todos os termos",
    indexTitle: "Glossário Looksmax — 21 Termos de Análise Facial | Fazly",
    indexDescription:
      "Todo termo de que você precisa para ler um relatório facial, definido com clareza: canthal tilt, fWHR, ângulo gonial, mewing, PSL e mais 16.",
    indexH1: "O glossário looksmax",
    indexStandfirst:
      "Vinte e um termos, definidos com clareza, com o folclore separado da geometria.",
  },
};
